import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { EventEmitter, ElementRef, ChangeDetectorRef, ViewContainerRef, NgZone, Directive, Input, Output } from '@angular/core';
import { asArray } from '@ng-doc/core/helpers/as-array';
import { isPresent } from '@ng-doc/core/helpers/is-present';
import { tooltipOpenAnimation, tooltipCloseAnimation } from '@ng-doc/ui-kit/animations';
import { NgDocOverlayContainerComponent } from '@ng-doc/ui-kit/components/overlay-container';
import { toElement } from '@ng-doc/ui-kit/helpers';
import { ngDocZoneOptimize, ngDocZoneDetach } from '@ng-doc/ui-kit/observables';
import * as i1 from '@ng-doc/ui-kit/services';
import { NgDocOverlayService } from '@ng-doc/ui-kit/services';
import * as i2 from '@ng-doc/ui-kit/services/overlay-strategy';
import { NgDocOverlayStrategy } from '@ng-doc/ui-kit/services/overlay-strategy';
import { NgDocOverlayUtils } from '@ng-doc/ui-kit/utils';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { fromEvent, timer, merge, EMPTY } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
let NgDocTooltipDirective = /*#__PURE__*/(() => {
  let NgDocTooltipDirective = class NgDocTooltipDirective {
    constructor(elementRef, changeDetectorRef, viewContainerRef, overlayService, ngZone, scrollStrategy) {
      this.elementRef = elementRef;
      this.changeDetectorRef = changeDetectorRef;
      this.viewContainerRef = viewContainerRef;
      this.overlayService = overlayService;
      this.ngZone = ngZone;
      this.scrollStrategy = scrollStrategy;
      this.content = '';
      this.delay = 1000;
      this.positions = ['top-center', 'bottom-center', 'right-center', 'left-center'];
      this.canOpen = true;
      this.panelClass = '';
      this.minHeight = '';
      this.maxHeight = '';
      this.height = '';
      this.minWidth = '';
      this.maxWidth = '';
      this.width = '';
      this.beforeOpen = new EventEmitter();
      this.afterOpen = new EventEmitter();
      this.beforeClose = new EventEmitter();
      this.afterClose = new EventEmitter();
      this.overlayRef = null;
    }
    ngAfterViewInit() {
      // Opens tooltip with delay
      fromEvent(this.pointerOriginElement, 'mouseenter').pipe(filter(() => this.canOpen && !this.isOpened), switchMap(() => timer(this.delay).pipe(takeUntil(fromEvent(this.pointerOriginElement, 'mouseleave')))), ngDocZoneOptimize(this.ngZone), untilDestroyed(this)).subscribe(() => this.show());
      // Closes tooltip when mouseleave was fired, and cancel closing if mouseenter was happened
      merge(fromEvent(this.pointerOriginElement, 'mouseleave'), this.beforeOpen.pipe(switchMap(() => isPresent(this.overlayRef) ? fromEvent(this.overlayRef.overlayRef.overlayElement, 'mouseleave') : EMPTY))).pipe(filter(() => this.isOpened), switchMap(() => timer(50).pipe(takeUntil(fromEvent(this.pointerOriginElement, 'mouseenter')), takeUntil(isPresent(this.overlayRef) ? fromEvent(this.overlayRef.overlayRef.overlayElement, 'mouseenter') : EMPTY))), untilDestroyed(this), ngDocZoneOptimize(this.ngZone)).subscribe(() => this.hide());
    }
    show() {
      if (!this.isOpened) {
        this.overlayRef = this.overlayService.open(this.content, {
          origin: this.displayOriginElement,
          overlayContainer: NgDocOverlayContainerComponent,
          positionStrategy: this.overlayService.connectedPositionStrategy(this.displayOriginElement, this.getPositions(this.positions)),
          viewContainerRef: this.viewContainerRef,
          withPointer: true,
          contactBorder: true,
          panelClass: ['ng-doc-tooltip', ...asArray(this.panelClass)],
          height: this.height,
          width: this.width,
          minHeight: this.minHeight,
          minWidth: this.minWidth,
          maxHeight: this.maxHeight,
          maxWidth: this.maxWidth,
          scrollStrategy: this.scrollStrategy,
          disposeOnRouteNavigation: true,
          openAnimation: tooltipOpenAnimation,
          closeAnimation: tooltipCloseAnimation
        });
        this.beforeOpen.emit();
        this.overlayRef?.afterOpen().pipe(ngDocZoneDetach(this.ngZone)).subscribe(() => this.afterOpen.emit());
        this.overlayRef?.beforeClose().pipe(ngDocZoneDetach(this.ngZone)).subscribe(() => this.beforeClose.emit());
        this.overlayRef?.afterClose().pipe(ngDocZoneDetach(this.ngZone)).subscribe(() => this.afterClose.emit());
        this.overlayRef?.beforeClose().subscribe(() => this.hide());
        this.changeDetectorRef.markForCheck();
      }
    }
    hide() {
      if (this.isOpened) {
        this.overlayRef?.close();
        this.overlayRef = null;
        this.changeDetectorRef.markForCheck();
      }
    }
    get isOpened() {
      return !!this.overlayRef;
    }
    ngOnDestroy() {
      if (this.overlayRef) {
        this.overlayRef.overlayRef.dispose();
      }
    }
    get pointerOriginElement() {
      return isPresent(this.pointerOrigin) ? toElement(this.pointerOrigin) : toElement(this.elementRef);
    }
    get displayOriginElement() {
      return isPresent(this.displayOrigin) ? toElement(this.displayOrigin) : toElement(this.elementRef);
    }
    getPositions(positions) {
      return NgDocOverlayUtils.getConnectedPosition(!!positions && asArray(positions).length ? positions : ['bottom-center', 'top-center', 'right-center', 'left-center'], this.displayOriginElement, 0, true);
    }
    static {
      this.ɵfac = function NgDocTooltipDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgDocTooltipDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i1.NgDocOverlayService), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i2.NgDocOverlayStrategy));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgDocTooltipDirective,
        selectors: [["", "ngDocTooltip", ""]],
        inputs: {
          content: [0, "ngDocTooltip", "content"],
          delay: "delay",
          displayOrigin: "displayOrigin",
          pointerOrigin: "pointerOrigin",
          positions: "positions",
          canOpen: "canOpen",
          panelClass: "panelClass",
          minHeight: "minHeight",
          maxHeight: "maxHeight",
          height: "height",
          minWidth: "minWidth",
          maxWidth: "maxWidth",
          width: "width"
        },
        outputs: {
          beforeOpen: "beforeOpen",
          afterOpen: "afterOpen",
          beforeClose: "beforeClose",
          afterClose: "afterClose"
        },
        exportAs: ["ngDocTooltip"],
        standalone: true
      });
    }
  };
  NgDocTooltipDirective = __decorate([UntilDestroy(), __metadata("design:paramtypes", [ElementRef, ChangeDetectorRef, ViewContainerRef, NgDocOverlayService, NgZone, NgDocOverlayStrategy])], NgDocTooltipDirective);
  return NgDocTooltipDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgDocTooltipDirective };
